import type { CSSProperties, ReactNode } from 'react';
import { adjustBrandColor, nextThemeBrandColors } from '../../../utils/brand-color/adjust-brand-color';
import { cx } from '../cx';

export type BrandSurfaceShape = 'strip' | 'rail' | 'hero';

export type BrandSurfaceProps = {
  /** The stored brand colour as extracted (or null). Never painted raw: it goes through adjustBrandColor. */
  color: string | null | undefined;
  shape: BrandSurfaceShape;
  children?: ReactNode;
  as?: 'div' | 'section' | 'header';
  className?: string;
};

type BrandVarName =
  | '--jc-brand-today'
  | '--jc-on-brand-today'
  | '--jc-brand-accent-today'
  | '--jc-brand-next'
  | '--jc-on-brand-next'
  | '--jc-brand-accent-next'
  | '--jc-brand-next-dark'
  | '--jc-on-brand-next-dark'
  | '--jc-brand-accent-next-dark';
type BrandVars = CSSProperties & Record<BrandVarName, string>;

/**
 * The CSS variables a brand surface sets, or null for the theme's fallback band. Carries today's
 * colours and the next theme's light (capped) and dark (brightness window) colours;
 * styles/jc-brand-surface.css picks the pair for the active theme into --jc-brand, --jc-on-brand
 * and --jc-brand-accent. Pure, exported for tests.
 */
export function brandSurfaceVars(color: string | null | undefined): BrandVars | null {
  const today = adjustBrandColor(color);
  const next = nextThemeBrandColors(color);
  if (today.mode === 'neutral-fallback' || !next) return null;
  return {
    '--jc-brand-today': today.panel,
    '--jc-on-brand-today': today.on,
    '--jc-brand-accent-today': today.accentOnWhite,
    '--jc-brand-next': next.light.panel,
    '--jc-on-brand-next': next.light.on,
    '--jc-brand-accent-next': next.light.accent,
    '--jc-brand-next-dark': next.dark.panel,
    '--jc-on-brand-next-dark': next.dark.on,
    '--jc-brand-accent-next-dark': next.dark.accent,
  };
}

const SHAPE: Record<BrandSurfaceShape, string> = {
  strip: 'h-14 rounded-t-jc-container px-4',
  rail: 'w-full p-5 md:w-52.5 md:rounded-jc-container',
  hero: 'rounded-jc-container p-6 md:p-10',
};

/**
 * The employer's own colour as a solid surface: a strip on cards, a rail on the job page, a hero on
 * the company page. The colour is adjusted for contrast and text picks ink or white by measurement;
 * employers with no usable colour get the theme's fallback band. Children can use `--jc-brand`,
 * `--jc-on-brand` and `--jc-brand-accent` (the brand colour safe as text on white).
 */
export function BrandSurface({ color, shape, children, as: Tag = 'div', className }: BrandSurfaceProps) {
  const vars = brandSurfaceVars(color);
  return (
    <Tag
      style={vars ?? undefined}
      data-brand={vars ? 'own' : 'fallback'}
      className={cx(
        'jc-brand-surface',
        vars ? 'bg-(--jc-brand) text-(--jc-on-brand)' : 'bg-jc-brand-fallback text-jc-on-brand-fallback',
        SHAPE[shape],
        className,
      )}
    >
      {children}
    </Tag>
  );
}
